import { useMemo } from "react"
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend,
    DoughnutController,
    type Plugin as ChartPlugin,
    TooltipOptions,
    Chart,
} from "chart.js"
import { Doughnut } from "react-chartjs-2"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { DashboardDataCard, DashboardDataContents } from "../../../../../dashboards/components/DashboardDataCard"
import { CheckinSummary } from "../../merchantHomeTypes"
import { MerchantHomeQuery, fetchCheckInSummary } from "../../merchantHomeApi"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { Box } from "@mui/system"

ChartJS.register(DoughnutController, ArcElement, ChartTooltip, Legend)

type ChartProps = Readonly<{
    summary: CheckinSummary
}>

const getOrCreateTooltip = (chart: Chart): HTMLDivElement => {
    let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLDivElement>("div[data-chartjs-tooltip]")

    if (!tooltipEl) {
        tooltipEl = document.createElement("div")
        tooltipEl.setAttribute("data-chartjs-tooltip", "1")
        tooltipEl.style.cssText = `
            background: rgba(0, 0, 0, 0.8);
            border-radius: 6px;
            color: white;
            font-size: 0.75rem;
            padding: 6px 10px;
            pointer-events: none;
            position: absolute;
            transform: translate(-50%, 0);
            transition: opacity 0.1s ease;
            white-space: nowrap;
            z-index: 10;
        `
        chart.canvas.parentNode?.appendChild(tooltipEl)
    }

    return tooltipEl
}

const externalTooltipHandler: TooltipOptions<"doughnut">["external"] = ({ chart, tooltip }) => {
    const tooltipEl = getOrCreateTooltip(chart)

    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = "0"
        return
    }

    if (tooltip.body && tooltip.dataPoints) {
        const dataPoint = tooltip.dataPoints[0]
        const color = (dataPoint.dataset.backgroundColor as string[])?.[dataPoint.dataIndex] ?? "#fff"
        const lines = tooltip.body.flatMap((b) => b.lines)

        tooltipEl.innerHTML = [
            tooltip.title?.[0] ? `<div style="font-weight: 600; margin-bottom: 4px">${tooltip.title[0]}</div>` : "",
            ...lines.map(
                (line) => `
                <div style="display: flex; align-items: center; gap: 6px">
                    <span style="
                        display: inline-block;
                        width: 10px;
                        height: 10px;
                        background: ${color};
                        flex-shrink: 0;
                    "></span>
                    <span>${line}</span>
                </div>`,
            ),
        ].join("")
    }

    const { offsetLeft: canvasX, offsetTop: canvasY } = chart.canvas
    const { chartArea } = chart

    const centerX = (chartArea.left + chartArea.right) / 2
    const centerY = (chartArea.top + chartArea.bottom) / 2
    const outerRadius = Math.min(chartArea.width, chartArea.height) / 2

    const angle = Math.atan2(tooltip.caretY - centerY, tooltip.caretX - centerX)
    const distance = outerRadius
    const tipX = centerX + Math.cos(angle) * distance
    const tipY = centerY + Math.sin(angle) * distance

    const tooltipHeight = tooltipEl.offsetHeight

    const top = Math.sin(angle) < 0 ? canvasY + tipY - tooltipHeight - 6 : canvasY + tipY + 6

    tooltipEl.style.opacity = "1"
    tooltipEl.style.left = `${canvasX + tipX}px`
    tooltipEl.style.top = `${top}px`
}

export function CheckInChart({ summary }: ChartProps) {
    const { checkInsPercentage, noCheckInsPercentage, numberOfCheckIns, numberOfOrders } = summary
    const chartKey = `${checkInsPercentage}-${noCheckInsPercentage}-${numberOfCheckIns}-${numberOfOrders}`

    const { l10n } = useAppLocalization()

    const plugins = useMemo<ChartPlugin<"doughnut">[]>(
        () => [
            {
                id: "center-text",
                afterDraw(chart, _args, _options) {
                    const width = chart.chartArea.width
                    const height = chart.chartArea.height
                    const ctx = chart.ctx

                    ctx.restore()
                    ctx.font = `normal 500 1.25rem Roboto`
                    ctx.textBaseline = "top"

                    const text = `${checkInsPercentage}%`
                    const textDims = ctx.measureText(text)
                    const textHeight = textDims.actualBoundingBoxAscent + textDims.actualBoundingBoxDescent

                    const textX = Math.round((width - textDims.width) / 2)
                    const textY = Math.round((height - textHeight) / 2)

                    ctx.fillText(text, textX, textY)
                    ctx.save()
                },
            },
        ],
        [checkInsPercentage],
    )

    const data = useMemo(() => {
        return {
            labels: [
                l10n.getString("merchant-home-checkin-card-participants", { checkedIn: numberOfCheckIns }),
                l10n.getString("merchant-home-checkin-card-none-participants", {
                    notCheckedIn: numberOfOrders - numberOfCheckIns,
                }),
            ],
            datasets: [
                {
                    label: l10n.getString("merchant-home-checkin-card-participation-percentage"),
                    data: [checkInsPercentage, noCheckInsPercentage],
                    backgroundColor: ["#22C55E", "#FCA5A5"],
                    borderColor: ["#22C55E", "#FCA5A5"],
                    borderWidth: 1,
                },
            ],
        }
    }, [l10n, numberOfCheckIns, numberOfOrders, checkInsPercentage, noCheckInsPercentage])

    return (
        <Box style={{ position: "relative", width: "100%", height: "100%" }}>
            <Doughnut
                key={chartKey}
                data={data}
                options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    cutout: "65%",
                    plugins: {
                        legend: {
                            position: "bottom",
                            labels: {
                                boxWidth: 7,
                                boxHeight: 7,
                                usePointStyle: true,
                                useBorderRadius: true,
                                pointStyle: "circle",
                                textAlign: "center",
                            },
                        },
                        tooltip: {
                            enabled: false,
                            external: externalTooltipHandler,
                        },
                    },
                }}
                plugins={plugins}
            />
        </Box>
    )
}
type DataLoaderProps = Readonly<{
    periodInDays: number
}>

function DataLoader({ periodInDays }: DataLoaderProps) {
    const { merchantId } = useMerchant()

    const checkInSummary = useApiQuery(fetchCheckInSummary, {
        queryName: MerchantHomeQuery.STATISTICS_CHECK_IN_SUMMARY,
        dependencies: {
            merchantId,
            periodInDays,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    return <>{checkInSummary.data && <CheckInChart summary={checkInSummary.data.checkInSummary} />}</>
}

type PieChartCheckInsProps = Readonly<{
    title?: string
    periodInDays: number
}>

export function StatisticsPieChartCheckIns({ title, periodInDays }: PieChartCheckInsProps) {
    return (
        <LocalizedStrict id="merchant-home-checkin-card" attrs={{ title: !title, titleTooltip: true }}>
            <DashboardDataCard title={title} titleTooltip="Check-in tooltip" isHeighNormal={false}>
                <DashboardDataContents>
                    <DataLoader periodInDays={periodInDays} />
                </DashboardDataContents>
            </DashboardDataCard>
        </LocalizedStrict>
    )
}
